import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { naira } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { OrderSuccess } from "@/components/store/OrderSuccess";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const FIELDS = [
  { name: "name", label: "Full name", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone number", type: "tel", required: true },
  { name: "address", label: "Delivery address", type: "text", required: true, full: true },
  { name: "city", label: "City", type: "text" },
  { name: "state", label: "State", type: "text" },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", notes: "" });
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [appliedCode, setAppliedCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const total = Math.max(0, subtotal - discount);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setDiscount(data.discount);
      setAppliedCode(data.code);
      toast.success(`Coupon ${data.code} applied — you saved ${naira(data.discount)}`);
    } catch (e) {
      setDiscount(0);
      setAppliedCode(null);
      toast.error(e.response?.data?.detail || "Invalid coupon");
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your bag is empty");
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ productId: i.id, quantity: i.quantity })),
        customer: form,
        couponCode: appliedCode || undefined,
      };
      const { data: order } = await api.post("/orders", payload);

      if (order.paystackEnabled) {
        const { data: pay } = await api.post("/payments/initialize", {
          orderId: order.id,
          callbackUrl: `${window.location.origin}/payment/callback`,
        });
        window.location.href = pay.authorization_url;
        return;
      }
      clear();
      setSuccess(order);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) return <OrderSuccess order={success} />;

  if (items.length === 0) {
    return (
      <div className="pt-40 pb-24 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-4xl text-espresso mb-4">Your bag is empty</h1>
        <Link to="/shop" className="text-terracotta underline">Browse the collection</Link>
      </div>
    );
  }

  return (
    <div className="pt-28 md:pt-36 pb-24" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <h1 className="font-serif text-4xl md:text-6xl text-espresso tracking-tight mb-10">Checkout</h1>
        <form onSubmit={placeOrder} className="grid lg:grid-cols-5 gap-10">
          {/* Details */}
          <div className="lg:col-span-3">
            <p className="overline text-terracotta mb-5">Delivery details</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                  <label className="text-sm text-bark mb-1.5 block">{f.label}{f.required && " *"}</label>
                  <Input
                    type={f.type}
                    required={f.required}
                    data-testid={`checkout-${f.name}`}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="rounded-xl border-sandline bg-bone focus-visible:ring-terracotta"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-sm text-bark mb-1.5 block">Order notes (optional)</label>
                <Textarea
                  data-testid="checkout-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded-xl border-sandline bg-bone focus-visible:ring-terracotta"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="lg:col-span-2">
            <div className="bg-sand rounded-2xl p-6 sticky top-28">
              <p className="overline text-terracotta mb-5">Order summary</p>
              <div className="space-y-4 mb-5">
                {items.map((i) => (
                  <div key={i.id} className="flex gap-3 items-center" data-testid={`summary-item-${i.id}`}>
                    <img src={i.image} alt={i.name} className="w-14 h-16 object-cover rounded-lg bg-bone" />
                    <div className="flex-1">
                      <p className="text-espresso text-sm font-medium leading-tight">{i.name}</p>
                      <p className="text-bark text-sm">× {i.quantity}</p>
                    </div>
                    <span className="text-espresso text-sm font-medium">{naira(i.price * i.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 mb-5">
                <Input
                  placeholder="Coupon code"
                  data-testid="coupon-input"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  className="rounded-full border-sandline bg-bone focus-visible:ring-terracotta"
                />
                <Button type="button" onClick={applyCoupon} data-testid="apply-coupon" variant="outline" className="rounded-full border-espresso text-espresso hover:bg-espresso hover:text-bone">
                  Apply
                </Button>
              </div>

              <div className="space-y-2 border-t border-sandline pt-4 text-sm">
                <div className="flex justify-between text-bark"><span>Subtotal</span><span>{naira(subtotal)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-moss" data-testid="checkout-discount"><span>Discount ({appliedCode})</span><span>- {naira(discount)}</span></div>
                )}
                <div className="flex justify-between text-espresso font-serif text-2xl pt-2">
                  <span>Total</span><span data-testid="checkout-total">{naira(total)}</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="place-order-button"
                className="w-full mt-6 rounded-full bg-terracotta text-bone hover:bg-espresso h-12 text-base"
              >
                {loading ? "Processing…" : "Place order"}
              </Button>
              <p className="text-bark text-xs text-center mt-3">
                Secure checkout · By ordering you agree to our{" "}
                <Link to="/policies/terms" className="underline hover:text-terracotta">Terms</Link> &{" "}
                <Link to="/policies/refund" className="underline hover:text-terracotta">Refund Policy</Link>
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
